/**
 * LoRA et VAE : ce que la machine qui génère possède, et ce que la requête
 * emporte.
 *
 * L'inventaire décrit la machine qui fera l'image : sur le site, la machine
 * appairée (son heartbeat porte ses LoRA et VAE, lus dans les fichiers
 * mêmes) ; en local, ce serveur (/api/components). Jamais le disque de la VM.
 * La sélection vit dans le store et voyage dans chaque requête image : la
 * machine l'applique pour cette image seulement.
 */
import { APIClient } from "../api/client.js";
import type { EmbeddingSelection, LoraSelection, VaeSelection } from "../state/store.js";
import { store } from "../state/store.js";
import type {
	EmbeddingRef,
	LocalComponent,
	LoraRef,
	VaeRef,
	WorkerCapabilities,
} from "../types/index.js";
import { serverGenerates } from "./execution.js";

/** Un composant qui ne dit pas son architecture est laissé au jugement de la machine. */
export function componentFits(architectures: string[], architecture: string | undefined): boolean {
	return !architecture || architectures.length === 0 || architectures.includes(architecture);
}

/** La machine appairée qui fera l'image, dans sa version la plus récente. */
function targetWorker(): WorkerCapabilities | null {
	const target = store.get().executionTarget;
	if (target.kind !== "hosted") return null;
	return (
		store.get().workers.find((worker) => worker.worker_id === target.worker.worker_id) ??
		target.worker
	);
}

/** Les LoRA et VAE de la machine qui générera. */
export async function machineComponents(): Promise<LocalComponent[]> {
	if (serverGenerates() && store.get().executionTarget.kind === "local") {
		return APIClient.getComponents();
	}
	return targetWorker()?.components ?? [];
}

export function selectionKey(entry: { source: string; weight_name?: string | null }): string {
	return entry.weight_name ? `${entry.source}::${entry.weight_name}` : entry.source;
}

/** Ce que la requête emporte pour ce modèle : seulement ce qui lui convient. */
export function componentsForRequest(architecture: string | undefined): {
	loras: LoraRef[];
	vae: VaeRef | null;
	embeddings: EmbeddingRef[];
} {
	const { loras, vae, embeddings } = store.get();
	return {
		embeddings: embeddings
			.filter((entry: EmbeddingSelection) => componentFits(entry.architectures, architecture))
			.map((entry) => ({
				source: entry.source,
				weight_name: entry.weight_name ?? null,
				token: entry.token,
			})),
		loras: loras
			.filter((lora: LoraSelection) => componentFits(lora.architectures, architecture))
			.map((lora) => ({
				source: lora.source,
				weight_name: lora.weight_name ?? null,
				weight: lora.weight,
			})),
		vae:
			vae && componentFits(vae.architectures, architecture)
				? { source: vae.source, subfolder: vae.subfolder ?? null }
				: null,
	};
}

/** `auteur/nom`, `auteur/nom::fichier.safetensors` ou `fichier.safetensors`. */
export function parseLoraInput(value: string): { source: string; weight_name?: string } | null {
	const text = value.trim();
	const [source, weightName] = text.split("::", 2);
	const repo = /^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/;
	const file = /^[^/\\:]+\.safetensors$/;
	if (!(repo.test(source) || file.test(source))) return null;
	if (weightName !== undefined && !/\.safetensors$/.test(weightName)) return null;
	return weightName ? { source, weight_name: weightName } : { source };
}

/** `auteur/nom[::fichier.safetensors][=mot-clé]` ou `fichier.safetensors[=mot-clé]`. */
export function parseEmbeddingInput(value: string): EmbeddingRef | null {
	const [where, token] = value.trim().split("=", 2);
	const parsed = parseLoraInput(where);
	if (!parsed) return null;
	if (token !== undefined && !/^<?[A-Za-z0-9_.-]{1,60}>?$/.test(token.trim())) return null;
	return { ...parsed, token: token?.trim() || null };
}

/** `auteur/nom` ou `auteur/nom:sous-dossier`. */
export function parseVaeInput(value: string): VaeSelection | null {
	const [source, subfolder] = value.trim().split(":", 2);
	if (!/^[A-Za-z0-9_.-]+\/[A-Za-z0-9_.-]+$/.test(source)) return null;
	return { source, subfolder: subfolder || null, name: value.trim(), architectures: [] };
}
